/**
 * The full, deterministic booking terms for one rate (ELARA-829):
 *  - Cancellation — refundable badge + the binding GDS terms, with the two-case deadline: an exact instant
 *    rendered in the guest's own timezone when Sabre gives an `AbsoluteDeadline`, or an honest
 *    "N before check-in (hotel local time)" when Sabre only gives a relative offset (no time-of-day).
 *  - Deposit and Guarantee — the structured terms.
 *  - The room-enrichment agent's plain-language summary beneath the binding cancellation line (kept as a
 *    friendly summary, never as the binding text).
 *
 * Rendered both inline on the Payment step (so the terms are 100% visible before the guest commits) and
 * inside the "Full policies" dialog on the summary, so the two surfaces can never drift apart.
 */

import type { RoomRateModel } from '@elara/client-core/models/RoomRateModel';

import React from 'react';

import { cn } from '@elara/client-core/lib/utils';

import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';
import {
  cancellationSummary,
  cancellationTierLines,
  cancellationVerbatim,
  describeDepositPolicy,
  describeGuaranteePolicy,
} from '#views/checkout/policy-utils.js';

export function CheckoutPolicyDetails({
  rate,
  checkInDate,
  className,
}: {
  rate: RoomRateModel | undefined;
  checkInDate?: string;
  className?: string;
}): React.ReactNode {
  if (!rate) return null;

  const cancellation = rate.cancellationPolicyDescription;
  const deposit = describeDepositPolicy(rate);
  const guarantee = describeGuaranteePolicy(rate);
  // Beneath the structured binding line (which gives the concrete DATE), show the hotel's VERBATIM rule —
  // it carries the exact cutoff TIME when stated ("by 4 PM one day prior"). Fall back to the LLM paraphrase
  // only when there's no verbatim text and there are structured terms above it (so it sits beneath the
  // binding line rather than duplicating it).
  const verbatim = cancellationVerbatim(rate);
  const detail = verbatim ?? ((rate.cancellationPolicies?.length ?? 0) > 0 ? cancellationSummary(rate) : undefined);
  // The hotel's tiers as loaded, one line each: what applies when, under the words.
  const tiers = cancellationTierLines(rate, checkInDate);
  // The headline alone is worth a section: a rate with no prose and no structured policies still has to
  // state whether it can be refunded.
  const hasCancellation = Boolean(rate.cancellationPolicyTitle || cancellation || detail);

  if (!hasCancellation && !deposit && !guarantee) return null;

  return (
    <div className={cn('space-y-4 text-sm', className)}>
      {hasCancellation ? (
        <section>
          <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Cancellation</h3>
          <p
            className={cn(
              'mt-1 flex items-center gap-1 font-medium',
              rate.refundStatus === 'refundable' && 'text-success',
            )}
          >
            <MaterialIcon
              icon={
                rate.refundStatus === 'unconfirmed' ? 'schedule' : rate.isRefundable ? 'currency_exchange' : 'money_off'
              }
              size={IconSizes.xs}
            />
            {/* The shared headline (with its deadline) — the same words every other surface shows. */}
            {rate.cancellationPolicyTitle}
          </p>
          {cancellation ? <p className="mt-1 text-pretty">{cancellation}</p> : null}
          {detail ? (
            <p className="mt-1 text-pretty text-muted-foreground">
              {/* Label the hotel's VERBATIM rule as such — but never the LLM paraphrase, which would
                  misrepresent Elara's summary as the property's exact words (ELARA-829 R1). */}
              {verbatim ? <span className="font-medium">Policy from hotel: </span> : null}
              {detail}
            </p>
          ) : null}
          {tiers.length > 0 ? (
            <ul className="mt-1 space-y-0.5 text-pretty text-muted-foreground">
              {tiers.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          ) : null}
        </section>
      ) : null}
      {deposit ? (
        <section>
          <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Deposit</h3>
          <p className="mt-1 text-pretty">{deposit}</p>
        </section>
      ) : null}
      {guarantee ? (
        <section>
          <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Guarantee</h3>
          <p className="mt-1 text-pretty">{guarantee}</p>
        </section>
      ) : null}
    </div>
  );
}
